import { useTranslation } from '@dreamstack/i18n'
import { ChevronLeft } from '@dreamstack/icons/src/icon-components/ChevronLeft'
import { ChevronRight } from '@dreamstack/icons/src/icon-components/ChevronRight'
import { cn } from '@dreamstack/util/src/cn'
import { Disclosure } from '@headlessui/react'
import CloseIcon from '@mui/icons-material/Close'
import type { FunctionComponent } from 'react'
import { BrokerAvatar } from './BrokerAvatar'
import { Text_13_18 } from '../layout/Texts'

const chevronClass = (small?: boolean) =>
  cn(
    'text-accentroWhite-full cursor-pointer transform -rotate-90 md:rotate-0 h-px-40 w-px-40',
    small
      ? 'md:h-px-24 md:w-px-24 lg:h-px-40 lg:w-px-40 md:mt-0'
      : 'md:mt-px-8 lg:h-px-40 lg:w-px-40'
  )

const disclosureButtonClass = (opts: {
  small?: boolean
  morePadding?: boolean
}) =>
  cn(
    'py-px-16 w-full flex items-center justify-between focus:outline-none md:flex-col md:px-0',
    opts.small
      ? 'px-px-16 md:w-px-40 lg:w-px-80 lg:px-px-16'
      : 'px-px-16 md:w-px-80',
    opts.morePadding && 'px-px-24 md:px-px-16'
  )

const disclosureWrapperClass = (isProject?: boolean) =>
  cn(
    isProject ? '-mt-px-40 md:-mt-px-64' : 'mt-px-40 md:mt-px-64',
    'z-10 col-span-full md:absolute md:h-px-320 bg-accentroGray-full right-0 rounded-none focus:outline-none flex'
  )

const chevronWrapperClass = (small?: boolean) =>
  cn(small && 'md:pt-px-20 lg:py-px-8')

const contactPersonTextClass = (small?: boolean) =>
  cn(
    'text-accentroWhite-full transform font-semibold text-base tracking-wide md:vertical-lr md:rotate-180',
    small ? 'md:pb-px-32 lg:pb-px-24' : 'md:pb-px-24'
  )

const panelClass = (small?: boolean) =>
  cn(
    'w-full h-auto px-px-24 pb-px-16 md:w-60 md:h-full md:p-px-20',
    small &&
      'md:border-t-2 md:border-l-2 md:border-b-2 md:border-accentroWhite-200'
  )

export const PropertyContact: FunctionComponent<
  React.PropsWithChildren<{
    small?: boolean
    morePadding?: boolean
    isProject?: boolean
    broker_email?: string | null
    broker_avatar_url?: string | null
    broker_first_name?: string | null
    broker_last_name?: string | null
    broker_phone?: string | null
  }>
> = ({
  broker_email,
  broker_avatar_url,
  broker_first_name,
  broker_last_name,
  broker_phone,
  small,
  morePadding,
  isProject,
}) => {
  const brokerEmail = broker_email ?? 'kundenmanagement@accentro.de'
  const brokerEmailWithSoftBreak = brokerEmail.split('@').join('&#8203;@')
  const name = broker_last_name
    ? [broker_first_name, broker_last_name].join(' ')
    : 'Accentro Info'
  const phone = broker_phone ?? '030 - 88 71 81 41'
  const t = useTranslation()
  return (
    <Disclosure as="div" className={disclosureWrapperClass(isProject)}>
      {({ open }: { open: boolean }) => (
        <div className="flex flex-col md:flex-row">
          <div className={cn(open && 'md:hidden')}>
            <Disclosure.Button
              className={disclosureButtonClass({
                small,
                morePadding,
              })}
            >
              <BrokerAvatar extraSmall={small} url={broker_avatar_url} />
              <span className={contactPersonTextClass(small)}>
                {t('accentro:contactPerson')}
              </span>
              <div className={chevronWrapperClass(small)}>
                {open ? (
                  <ChevronRight className={chevronClass(small)} />
                ) : (
                  <ChevronLeft className={chevronClass(small)} />
                )}
              </div>
            </Disclosure.Button>
          </div>
          <Disclosure.Panel className={panelClass(small)}>
            <div className="hidden md:flex md:justify-between md:items-center">
              <Text_13_18 className="font-semibold text-accentroWhite-full">
                {t('accentro:contactPerson')}
              </Text_13_18>
              <Disclosure.Button className="text-accentroWhite-full focus:outline-none">
                <CloseIcon />
              </Disclosure.Button>
            </div>
            <div className="h-full flex flex-col justify-center space-y-px-16 md:items-start md:space-y-px-24">
              <div className="hidden md:block">
                <BrokerAvatar big url={broker_avatar_url} />
              </div>
              <div className="text-accentroWhite-full font-semibold text-px-20 md:text-px-16">
                {name}
              </div>
              <div className="flex flex-col space-y-px-8">
                <div>
                  <Text_13_18 className="font-semibold text-accentroWhite-full">
                    {t('accentro:contact.phone')}
                  </Text_13_18>
                  <a href={`tel:${phone}`}>
                    <Text_13_18 className="text-accentroWhite-full">
                      {phone}
                    </Text_13_18>
                  </a>
                </div>
                <div>
                  <Text_13_18 className="font-semibold text-accentroWhite-full">
                    {t('accentro:contact.email')}
                  </Text_13_18>
                  <a href={`mailto:${brokerEmail}`}>
                    <Text_13_18 className="text-accentroAquaMidLight-full font-semibold break-words">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: brokerEmailWithSoftBreak,
                        }}
                      />
                    </Text_13_18>
                  </a>
                </div>
              </div>
            </div>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  )
}
